import { Injectable, inject } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { map, switchMap, tap, first } from 'rxjs/operators';

import { TimeService } from '@shared';

import {
    ProblemInstanceService,
    TaskUpdate,
    TravelTimeImprovementService,
    TravelTimeImprovementSuggestion,
    SuggestionInformation
} from 'planner-shared';

import { TravelTimeImprovementSuggestionView } from '../models/travel-time-improvement-suggestion-view.model';
import { TravelTimeImprovementSuggestionsView } from '../models/travel-time-improvement-suggestions-view.model';

@Injectable({
    providedIn: 'root'
})
export class TravelTimeImprovementSuggestionService {
    private readonly travelTimeImprovementService = inject(TravelTimeImprovementService);
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly timeService = inject(TimeService);

    private improvementIds: string[];
    private currentImprovementId: string;
    private readonly travelTimeImprovementSuggestions$: ReplaySubject<TravelTimeImprovementSuggestionsView>;
    private readonly travelTimeImprovementSuggestionsCount$: ReplaySubject<number>;

    constructor() {
        this.travelTimeImprovementSuggestionsCount$ = new ReplaySubject(1);
        this.travelTimeImprovementSuggestions$ = new ReplaySubject(1);
    }

    public loadSuggestions(): Observable<void> {
        return this.travelTimeImprovementService.getTravelTimeImprovements().pipe(
            switchMap(result => {
                if (result.improvementIds && result.improvementIds.length > 0) {
                    this.improvementIds = result.improvementIds;
                    return this.takeNextSuggestions();
                }
                return of(<TravelTimeImprovementSuggestionsView>{});
            }),
            tap(result => {
                const suggestionsCount = result.suggestions ? result.suggestions.length : 0;
                this.travelTimeImprovementSuggestionsCount$.next(suggestionsCount);
                this.travelTimeImprovementSuggestions$.next(result);
            }),
            map(() => null)
        );
    }

    public getSuggestions(): Observable<TravelTimeImprovementSuggestionsView> {
        return this.travelTimeImprovementSuggestions$.asObservable().pipe(first());
    }

    public acceptSuggestions(
        suggestions: TravelTimeImprovementSuggestionView[]
    ): Observable<TravelTimeImprovementSuggestionsView> {
        const suggestionInformationList = suggestions.map(suggestion => this.mapSuggestionInformation(suggestion));

        return this.travelTimeImprovementService
            .handleTravelTimeImprovement(this.currentImprovementId, suggestionInformationList)
            .pipe(
                switchMap(result => {
                    const suggestionTasksToUpdate = suggestions
                        .filter(suggestion => suggestion.checked)
                        .map(suggestion => this.mapTaskUpdate(suggestion));

                    return this.updateProblemInstancePatientTasks(suggestionTasksToUpdate).pipe(
                        map(() => result.updateRoutes)
                    );
                }),
                switchMap(updateRoutes => {
                    if (updateRoutes) {
                        return this.loadSuggestions().pipe(switchMap(() => this.getSuggestions()));
                    }
                    return this.getNextSuggestions();
                })
            );
    }

    public rejectAllCurrentAndGetNewSuggestions(
        suggestions: TravelTimeImprovementSuggestionView[]
    ): Observable<TravelTimeImprovementSuggestionsView> {
        const suggestionInformationList = suggestions.map(suggestion =>
            this.mapSuggestionInformation(suggestion, true)
        );

        return this.travelTimeImprovementService
            .handleTravelTimeImprovement(this.currentImprovementId, suggestionInformationList)
            .pipe(switchMap(() => this.getNextSuggestions()));
    }

    public getTravelTimeImprovementSuggestionsCount(): Observable<number> {
        return this.travelTimeImprovementSuggestionsCount$.asObservable();
    }

    private getNextSuggestions(): Observable<TravelTimeImprovementSuggestionsView> {
        return this.takeNextSuggestions().pipe(
            switchMap(result => {
                if (result.suggestions && result.suggestions.length > 0) {
                    this.travelTimeImprovementSuggestionsCount$.next(result.suggestions.length);
                    return of(result);
                }
                return this.loadSuggestions().pipe(switchMap(() => this.getSuggestions()));
            })
        );
    }

    private takeNextSuggestions(): Observable<TravelTimeImprovementSuggestionsView> {
        if (this.improvementIds.length === 0) {
            return of(<TravelTimeImprovementSuggestionsView>{});
        }

        this.currentImprovementId = this.improvementIds.shift();

        return this.travelTimeImprovementService.getTravelTimeImprovement(this.currentImprovementId).pipe(
            map(result => {
                return <TravelTimeImprovementSuggestionsView>{
                    travelTimeSuggestion: Math.floor(result.travelTimeDelta / 60),
                    suggestions: result.suggestionsResponse.suggestions.map(suggestion =>
                        this.mapSuggestion(suggestion)
                    )
                };
            })
        );
    }

    private updateProblemInstancePatientTasks(tasks: TaskUpdate[]): Observable<void> {
        return this.problemInstanceService.getProblemInstance().pipe(
            tap(result => {
                tasks.forEach(task => {
                    const patientTask = result.problemInstance.tasks.find(i => i.taskId === task.taskId);
                    if (patientTask) {
                        patientTask.fromTime = task.startTime;
                        patientTask.toTime = task.endTime;
                    }
                });
            }),
            map(() => null)
        );
    }

    private mapSuggestion(suggestion: TravelTimeImprovementSuggestion): TravelTimeImprovementSuggestionView {
        const suggestionView = Object.assign(<TravelTimeImprovementSuggestionView>{}, suggestion);
        suggestionView.permanent = true;
        suggestionView.checked = true;
        suggestionView.proposedStartDate = suggestion.proposedStartDate;
        suggestionView.proposedEndDate = suggestion.proposedEndDate;
        suggestionView.originalStartTime = this.timeService.convertTime(suggestion.originalStartTime, true);
        suggestionView.originalEndTime = this.timeService.convertTime(suggestion.originalEndTime, true);
        suggestionView.proposedStartTime = this.timeService.convertTime(suggestion.proposedStartTime, true);
        suggestionView.proposedEndTime = this.timeService.convertTime(suggestion.proposedEndTime, true);
        suggestionView.originalProposedStartTime = suggestionView.proposedStartTime;
        suggestionView.originalProposedEndTime = suggestionView.proposedEndTime;

        return suggestionView;
    }

    private mapSuggestionInformation(
        timeWindowSuggestion: TravelTimeImprovementSuggestionView,
        rejectAll?: boolean
    ): SuggestionInformation {
        return <SuggestionInformation>{
            taskId: timeWindowSuggestion.taskId,
            proposedStartDate: timeWindowSuggestion.proposedStartDate,
            proposedEndDate: timeWindowSuggestion.proposedEndDate,
            proposedStartTime: this.timeService.convertTime(timeWindowSuggestion.proposedStartTime),
            proposedEndTime: this.timeService.convertTime(timeWindowSuggestion.proposedEndTime),
            accept: rejectAll ? false : timeWindowSuggestion.checked,
            permanently: rejectAll ? false : timeWindowSuggestion.permanent
        };
    }

    private mapTaskUpdate(suggestion: TravelTimeImprovementSuggestionView): TaskUpdate {
        return <TaskUpdate>{
            taskId: suggestion.taskId,
            startTime: this.timeService.convertTime(suggestion.proposedStartTime),
            endTime: this.timeService.convertTime(suggestion.proposedEndTime),
            duration: suggestion.duration,
            skillRequirement: suggestion.skillRequirement,
            numberOfEmployees: suggestion.numberOfEmployees,
            permanent: suggestion.permanent
        };
    }
}
